import {Card, Flag, Placement, useAnimatedPopper} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import styled from 'styled-components';

import {isNotNil} from 'ramda-adjunct';

import {SourcingVehicleResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {PriceChart} from '@dms/shared';

import {
  buildArray,
  EMPTY_PLACEHOLDER,
  getSafely,
  parseDate,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

interface PriceChangesPopperProps extends TestIdProps {
  vehicle: SourcingVehicleResponseBody;
  currentPrice: number;
  currency: string;
  popperPlacement?: Placement;
  usePortal?: boolean;
}

export function PriceChangesPopper(props: PriceChangesPopperProps) {
  const formatCurrency = useFormatCurrency();
  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: props.popperPlacement,
    usePortal: props.usePortal,
  });

  const vehiclePrice = props.currentPrice;
  const currency = props.currency;
  const firstScrapedPrice = getSafely(props.vehicle.priceChanges, 0) ?? vehiclePrice;
  const priceChanges =
    props.vehicle.priceChanges?.map((price, index) => ({
      price,
      date: parseDate(getSafely(props.vehicle.priceChangesDates, index)),
    })) ?? [];
  const chartData = buildArray<{date: number; price: number}>(
    priceChanges.map((change) => ({
      date: change.date?.getTime() ?? 0,
      price: change.price,
    }))
  ).when(priceChanges.length > 0, {
    date: new Date().getTime(),
    price: vehiclePrice,
  });
  const discount = firstScrapedPrice - vehiclePrice;
  const shouldShowPriceChanges = discount !== 0;
  const daysInAdvertisement = props.vehicle.daysOnStock;
  const nDaysText = isNotNil(daysInAdvertisement)
    ? i18n.t('entity.vehicle.labels.nDaysInAdvertisement', {
        count: daysInAdvertisement,
      })
    : EMPTY_PLACEHOLDER;

  return (
    <>
      <Show when={shouldShowPriceChanges} whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}>
        <PriceChangeBox
          ref={referenceRef}
          colorScheme={discount >= 0 ? 'orange' : 'red'}
          onMouseEnter={() => openPopper()}
          onMouseLeave={() => closePopper()}
          onFocus={() => openPopper()}
          onBlur={() => closePopper()}
          onClick={() => openPopper()}
          role="button"
          tabIndex={0}
          aria-label={i18n.t('entity.vehicle.labels.priceChanges')}
        >
          <Icon
            value={discount >= 0 ? 'action/trending_down' : 'action/trending_up'}
            size={3}
            color="general.white"
          />
        </PriceChangeBox>
      </Show>

      <Popper {...popperProps}>
        <Box width={['100vw', 133]} padding={[4, 0]}>
          <Card>
            <VStack spacing={4} width="100%">
              <HStack spacing={1} align="flex-start" justify="space-between" width="100%">
                <VStack spacing={1}>
                  <Heading size={4} color="primary">
                    {i18n.t('entity.vehicle.labels.priceChanges')}
                  </Heading>
                  <Text size="xSmall" color="secondary">
                    {nDaysText}
                  </Text>
                </VStack>
                <Flag
                  colorScheme={discount >= 0 ? 'orange' : 'red'}
                  size="small"
                  label={
                    discount >= 0
                      ? i18n.t('entity.vehicle.labels.discount', {
                          discount: formatCurrency(discount, currency),
                        })
                      : i18n.t('entity.vehicle.labels.priceIncrease', {
                          increase: formatCurrency(-discount, currency),
                        })
                  }
                  data-testid={suffixTestId('discount', props)}
                />
              </HStack>
              <Box width="100%" height={30}>
                <PriceChart chartData={chartData} currency={currency} />
              </Box>
            </VStack>
          </Card>
        </Box>
      </Popper>
    </>
  );
}

type PriceChangeBoxProps = {
  colorScheme: 'orange' | 'red';
};

// General Box component doesn't allow to set 2px padding
// eslint-disable-next-line eag/no-css-property
const PriceChangeBox = styled.div<PriceChangeBoxProps>`
  background-color: ${(props) => props.theme.colors.palettes[props.colorScheme][60][100]};
  border-radius: ${(props) => props.theme.radii.xSmall};
  padding: 2px;

  &:hover,
  &:focus-visible {
    background-color: ${(props) => props.theme.colors.palettes[props.colorScheme][70][100]};
  }
`;
